import { useEffect, useState, useMemo, useRef } from 'react';
import { resolveShareLink } from '@/api/share';
import { MindMapDocument } from '@/model/document';
import { getLayoutEngine } from '@/layout/layoutEngine';
import { measureTextSize } from '@/layout/measure';
import { ThemeProvider } from '@/theme/themeContext';
import { NodeRenderer } from '@/canvas/NodeRenderer';
import { ConnectorRenderer } from '@/canvas/ConnectorRenderer';
import { CrossLinkRenderer } from '@/canvas/CrossLinkRenderer';
import type { LayoutResult } from '@/layout/types';
import type { MindMapNode, Connection, MapMeta, MapSettings } from '@/model/types';

interface SharedMapViewerProps {
  token: string;
}

function SharedMapViewerInner({ token }: SharedMapViewerProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permission, setPermission] = useState<string>('view');
  const [nodes, setNodes] = useState<Map<string, MindMapNode>>(new Map());
  const [connections, setConnections] = useState<Map<string, Connection>>(new Map());
  const [meta, setMeta] = useState<MapMeta>({ title: '', rootNodeId: '', createdAt: '', updatedAt: '' });
  const [settings, setSettings] = useState<MapSettings>({
    layoutType: 'horizontal-tree',
    horizontalGap: 80,
    verticalGap: 16,
    themeId: 'default',
  });

  // Pan/zoom state
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const resolved = await resolveShareLink(token);
        if (cancelled) return;

        setPermission(resolved.permission);

        // Download the Yjs state from the download URL
        const resp = await fetch(resolved.downloadUrl);
        if (!resp.ok) throw new Error('Failed to download map data');

        const arrayBuffer = await resp.arrayBuffer();
        const state = new Uint8Array(arrayBuffer);

        const mapDoc = new MindMapDocument();
        mapDoc.applyState(state);

        if (cancelled) return;

        setNodes(mapDoc.getAllNodes());
        setConnections(mapDoc.getAllConnections());
        setMeta(mapDoc.getMeta());
        setSettings(mapDoc.getSettings());
        setLoading(false);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load shared map';
        setError(message);
        setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [token]);

  // Center on root after loading
  useEffect(() => {
    if (!loading && !error && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      setPanX(rect.width / 2);
      setPanY(rect.height / 2);
    }
  }, [loading, error]);

  // Compute layout
  const layout: LayoutResult = useMemo(() => {
    if (!meta.rootNodeId || nodes.size === 0) {
      return { nodeBounds: new Map(), connectorPaths: [], totalBounds: { x: 0, y: 0, width: 0, height: 0 } };
    }
    return getLayoutEngine(settings.layoutType).compute({
      nodes,
      rootNodeId: meta.rootNodeId,
      settings: {
        horizontalGap: settings.horizontalGap,
        verticalGap: settings.verticalGap,
        nodePaddingX: 16,
        nodePaddingY: 10,
      },
      measureText: measureTextSize,
    });
  }, [nodes, meta.rootNodeId, settings]);

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === svgRef.current || (e.target as SVGElement).classList.contains('canvas-bg')) {
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY, panX, panY };
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    setPanX(panStart.current.panX + dx);
    setPanY(panStart.current.panY + dy);
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Zoom via native listener with { passive: false }
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      e.stopPropagation();
      setZoom((prev) => Math.max(0.1, Math.min(3, prev + (e.deltaY > 0 ? -0.05 : 0.05))));
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  if (loading) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
        color: '#94a3b8',
        fontFamily: 'system-ui, sans-serif',
        fontSize: 16,
      }}>
        Loading shared map...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
        color: '#e2e8f0',
        fontFamily: 'system-ui, sans-serif',
        gap: 12,
      }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Unable to load shared map</h2>
        <p style={{ margin: 0, fontSize: 14, color: '#94a3b8' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'relative' }}>
      {/* Header bar */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 16px',
        background: '#0f172a',
        borderBottom: '1px solid #1e293b',
        zIndex: 10,
        fontFamily: 'system-ui, sans-serif',
      }}>
        <span style={{ color: '#94a3b8', fontSize: 14, fontWeight: 600 }}>
          {meta.title}
        </span>
        {permission === 'view' && (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            background: '#334155',
            color: '#94a3b8',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            View only
          </span>
        )}
      </div>

      {/* Canvas */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ background: 'var(--bg-canvas)', cursor: isPanning ? 'grabbing' : 'default' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <rect className="canvas-bg" width="100%" height="100%" fill="transparent" />
          <g transform={`translate(${panX}, ${panY}) scale(${zoom})`}>
            {/* Connectors */}
            <g className="connectors">
              {layout.connectorPaths.map((cp) => (
                <ConnectorRenderer key={`${cp.parentId}-${cp.childId}`} path={cp.path} />
              ))}
            </g>

            {/* Cross-links */}
            <g className="cross-links">
              {Array.from(connections.values()).map((conn) => (
                <CrossLinkRenderer
                  key={conn.id}
                  connection={conn}
                  nodeBounds={layout.nodeBounds}
                />
              ))}
            </g>

            {/* Nodes (read-only - no click handlers that trigger editing) */}
            <g className="nodes">
              {Array.from(layout.nodeBounds.values()).map((bounds) => {
                const node = nodes.get(bounds.id);
                if (!node) return null;
                return (
                  <NodeRenderer
                    key={bounds.id}
                    node={node}
                    bounds={bounds}
                    isSelected={false}
                    isDropTarget={false}
                    isSearchMatch={false}
                    onClick={(_nodeId: string, _e: React.MouseEvent) => {/* read-only: no-op */}}
                    onDoubleClick={(_nodeId: string, _e: React.MouseEvent) => {/* read-only: no-op */}}
                    onMouseDown={(_nodeId: string, _e: React.MouseEvent) => {/* read-only: no-op */}}
                    onContextMenu={(_nodeId: string, _e: React.MouseEvent) => {/* read-only: no-op */}}
                  />
                );
              })}
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
}

export function SharedMapViewer({ token }: SharedMapViewerProps) {
  return (
    <ThemeProvider>
      <SharedMapViewerInner token={token} />
    </ThemeProvider>
  );
}
